import React, { useState, useEffect, useRef } from 'react';
import {
    IoSearchOutline, IoAddOutline, IoCloseOutline, IoChevronDown,
    IoMailOutline, IoBusinessOutline, IoLocationOutline,
    IoPersonOutline, IoBriefcaseOutline, IoCalendarOutline, IoIdCardOutline,
    IoChevronBack, IoChevronForward, IoEyeOutline, IoEye, IoClose,
    IoCheckmarkCircle, IoWarning,
    IoCloudUploadOutline, IoDownloadOutline, IoDocumentTextOutline,
    IoAlertCircleOutline, IoCloseCircle, IoRefreshOutline, IoOpenOutline,
    IoPencilOutline, IoPencil, IoPower, IoCheckboxOutline, IoSquareOutline,
    IoCheckmarkDoneOutline, IoAddCircleOutline, IoSaveOutline,
    IoTrashOutline, IoCardOutline, IoWalletOutline,
    IoArrowUp, IoArrowDown, IoTime // <--- Added Icons for Sorting
} from 'react-icons/io5';
import './onboarding.css';
import HRDocumentAssignment from './HRDocumentAssignment';

const OnboardingHR = () => {
    // --- CONFIG ---
    const API_URL = process.env.REACT_APP_API_URL;

    // --- IN-MEMORY CACHE ---
    let GLOBAL_MASTER_CACHE = null;
    const CACHE_DURATION = 60 * 60 * 1000; // 1 Hour

    const [selectedEmp, setSelectedEmp] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [masters, setMasters] = useState({
        departments: [],
        designations: [],
        vendorTypes: [],
        bloodGroups: [],
        states: [],
        statuses: [],
        sex: [],
        bankNames: []
    });
    const [employees, setEmployees] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, totalPages: 1, totalRecords: 0, limit: 10 });
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortOrder, setSortOrder] = useState('recent');
    const [viewMode, setViewMode] = useState('list');

    const [toast, setToast] = useState({ show: false, type: 'success', message: '' });
    const showToast = (type, message) => {
        setToast({ show: true, type, message });
        setTimeout(() => setToast(t => ({ ...t, show: false })), 3000);
    };

    const getAuthHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem('zTaskAuthToken') || ''}`
    });

    const [checklist, setChecklist] = useState([]);
    const [checklistLoading, setChecklistLoading] = useState(false);
    const [docsLoading, setDocsLoading] = useState(false);
    const [empDocuments, setEmpDocuments] = useState([]);

    const [submitting, setSubmitting] = useState(false);

    const [importFile, setImportFile] = useState(null);
    const [importErrors, setImportErrors] = useState([]);
    const [importSummary, setImportSummary] = useState(null);

    const [newTaskName, setNewTaskName] = useState('');
    const [isAddingTask, setIsAddingTask] = useState(false);

    const initialFormState = {
        first_name: '',
        last_name: '',
        emp_code: '',
        email: '',
        designation: '',
        department: '',
        vendor_type: '',
        address: '',
        city: '',
        state_id: '',
        pincode: '',
        birth_date: '',
        pan_no: '',
        blood_group: '',
        sex: '',
        status_id: '',
        current_salary: '',
        acno: '',
        ifsc_code: '',
        bankname_id: '',
        branch: '',
        contact_details: ''
    };

    const [formData, setFormData] = useState(initialFormState);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editEmpId, setEditEmpId] = useState(null);

    const fileInputRef = useRef(null);

    // --- SKELETON LOADER ---
    const TableSkeleton = () => (
        <div className="skeleton-wrapper">
            {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="skeleton-row">
                    <div className="skeleton-box" style={{ width: 60 }}></div>
                    <div className="skeleton-box" style={{ width: 150 }}></div>
                    <div className="skeleton-box" style={{ width: 100 }}></div>
                    <div className="skeleton-box" style={{ width: 80 }}></div>
                    <div className="skeleton-circle"></div>
                </div>
            ))}
        </div>
    );

    const Toast = ({ show, type, message, onClose }) => {
        if (!show) return null;
        return (
            <div className={`toast-notification show ${type || ''}`}>
                <div className="toast-content">
                    <div className="toast-msg">{message}</div>
                </div>
                <button className="toast-close" onClick={onClose}><IoClose size={18} /></button>
            </div>
        );
    };

    const SearchableSelect = ({ label, options, selectedVal, handleChange, placeholder, required, disabled }) => {
        return (
            <div className="select-container">
                <label className="form-label">{label} {required ? <span style={{ color: '#EF4444' }}>*</span> : null}</label>
                <select
                    className="form-input"
                    value={selectedVal ?? ''}
                    onChange={(e) => handleChange(e.target.value)}
                    disabled={disabled}
                >
                    <option value="">{placeholder || 'Select'}</option>
                    {(options || []).map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>
        );
    };

    const getErrorSuggestion = (errorMsg) => {
        if (!errorMsg) return "Check data validity.";
        const msg = errorMsg.toLowerCase();
        if (msg.includes('date')) return "Invalid Date Format. Use YYYY-MM-DD.";
        if (msg.includes('duplicate') || msg.includes('already')) return "Duplicate record. Check Email, Emp Code, or PAN.";
        if (msg.includes('required') || msg.includes('missing') || msg.includes('null')) return "Required field missing.";
        if (msg.includes('integer') || msg.includes('invalid input syntax')) return "Some fields require numeric IDs.";
        return "Check the field values.";
    };

    const getInitials = (first = '', last = '') => {
        const a = (first || '').trim();
        const b = (last || '').trim();
        return `${a ? a[0] : ''}${b ? b[0] : ''}`.toUpperCase() || 'NA';
    };

    const getLabel = (id, key) => {
        if (!id) return '-';
        const list = masters?.[key] || [];
        const found = list.find((x) => String(x.value) === String(id));
        return found?.label || '-';
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            const d = new Date(dateStr);
            if (!isNaN(d.getTime())) return d.toLocaleDateString('en-GB');
        } catch (e) {
            // ignore
        }
        return dateStr;
    };

    // ... (rest of the code remains the same)

    const fetchMasters = async () => {
        try {
            const now = new Date().getTime();
            if (GLOBAL_MASTER_CACHE && (now - GLOBAL_MASTER_CACHE.timestamp < CACHE_DURATION)) { setMasters(GLOBAL_MASTER_CACHE.data); return; }
            const h = { 'Content-Type': 'application/json', ...getAuthHeaders() };

            const [dept, desig, vend, blood, st, status, sexList, banks] = await Promise.all([
                fetch(`${API_URL}/get-column/departments/department_name`, { headers: h }).then(r => r.json()).catch(() => []),
                fetch(`${API_URL}/get-column/designation/designation_name`, { headers: h }).then(r => r.json()).catch(() => []),
                fetch(`${API_URL}/get-column/common_master/vendor_type`, { headers: h }).then(r => r.json()).catch(() => []),
                fetch(`${API_URL}/get-column/common_master/blood_group`, { headers: h }).then(r => r.json()).catch(() => []),
                fetch(`${API_URL}/get-column/state_master/state_name`, { headers: h }).then(r => r.json()).catch(() => []),
                fetch(`${API_URL}/get-column/common_master/employee_status`, { headers: h }).then(r => r.json()).catch(() => []),
                fetch(`${API_URL}/get-column/common_master/sex`, { headers: h }).then(r => r.json()).catch(() => []),
                fetch(`${API_URL}/get-column/common_master/bank_name`, { headers: h }).then(r => r.json()).catch(() => [])
            ]);

            const fmt = (arr) => Array.isArray(arr) ? arr.map(d => ({ value: d.id, label: d.value })) : [];

            const newMasters = {
                departments: fmt(dept), designations: fmt(desig), vendorTypes: fmt(vend),
                bloodGroups: fmt(blood), states: fmt(st), statuses: fmt(status), sex: fmt(sexList),
                bankNames: fmt(banks)
            };

            setMasters(newMasters); GLOBAL_MASTER_CACHE = { timestamp: new Date().getTime(), data: newMasters };
        } catch (e) { console.error("Master fetch error:", e); }
    };

    const fetchEmployees = async (pageNo = 1) => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/hr/employees?page=${pageNo}&limit=${pagination.limit}`, {
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }
            });
            if (!res.ok) throw new Error("API Error");
            const data = await res.json();
            if (data && data.data) {
                setEmployees(data.data);
                setPagination(p => ({
                    ...p,
                    page: data.pagination.currentPage,
                    totalRecords: data.pagination.totalRecords,
                    totalPages: data.pagination.totalPages
                }));
            }
        } catch (err) {
            showToast('error', 'Could not fetch employee list.');
            setEmployees([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchEmployeeDocs = async (userId) => {
        setDocsLoading(true);
        try {
            const res = await fetch(`${API_URL}/documents/${userId}`, { headers: getAuthHeaders() });
            const data = await res.json();
            if (res.ok) {
                setEmpDocuments(data.documents || []);
            } else {
                setEmpDocuments([]);
            }
        } catch (e) {
            console.error("Doc fetch error", e);
            setEmpDocuments([]);
        } finally {
            setDocsLoading(false);
        }
    };

    // ... (rest of the code remains the same)

    useEffect(() => {
        const stored = localStorage.getItem('zTaskUserDetails');
        if (stored) {
            try {
                const user = JSON.parse(stored);
                const r = (user.role?.name || user.role || '').toUpperCase();
                if (['ADMIN', 'SUPER_ADMIN', 'HR'].includes(r)) {
                    setIsAdmin(true);
                }
            } catch (e) {
                // ignore parse errors
            }
        }

        fetchMasters();
        fetchEmployees(1);
    }, []);

    // --- FETCH CHECKLIST ---
    const fetchChecklist = async (employeeId) => {
        setChecklistLoading(true);
        try {
            const res = await fetch(`${API_URL}/checklist/user/${employeeId}`, {
                headers: getAuthHeaders()
            });
            if (res.ok) {
                const data = await res.json();
                setChecklist(data);
            } else {
                setChecklist([]);
            }
        } catch (error) {
            console.error("Checklist Fetch Error", error);
        } finally {
            setChecklistLoading(false);
        }
    };

    // --- EFFECT: Fetch Details on Selection ---
    useEffect(() => {
        if (selectedEmp) {
            fetchChecklist(selectedEmp.id);
            fetchEmployeeDocs(selectedEmp.id);
        }
    }, [selectedEmp]);

    // --- TOGGLE SINGLE CHECKLIST ITEM ---
    const handleChecklistToggle = async (task) => {
        if (!selectedEmp) return;

        const newStatus = !task.is_completed;
        const updatedList = checklist.map(t =>
            t.master_task_id === task.master_task_id ? { ...t, is_completed: newStatus } : t
        );
        setChecklist(updatedList);

        try {
            const res = await fetch(`${API_URL}/checklist/user/status`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({
                    employeeId: selectedEmp.id,
                    masterTaskId: task.master_task_id,
                    is_completed: newStatus
                })
            });

            if (!res.ok) {
                setChecklist(checklist);
                showToast('error', 'Failed to update checklist status');
            }
        } catch (error) {
            setChecklist(checklist);
            showToast('error', 'Network error updating checklist');
        }
    };

    // --- MARK ALL / UNMARK ALL FUNCTION ---
    const handleMarkAll = async () => {
        if (!selectedEmp || checklist.length === 0) return;

        const allCompleted = checklist.every(t => t.is_completed);
        const targetStatus = !allCompleted;

        const originalChecklist = [...checklist];
        const updatedChecklist = checklist.map(t => ({ ...t, is_completed: targetStatus }));
        setChecklist(updatedChecklist);

        try {
            const tasksToUpdate = checklist.filter(t => t.is_completed !== targetStatus);
            if (tasksToUpdate.length === 0) return;

            const requests = tasksToUpdate.map(task =>
                fetch(`${API_URL}/checklist/user/status`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                    body: JSON.stringify({
                        employeeId: selectedEmp.id,
                        masterTaskId: task.master_task_id,
                        is_completed: targetStatus
                    })
                })
            );

            await Promise.all(requests);
            showToast('success', targetStatus ? 'All tasks marked as completed' : 'All tasks marked as pending');

        } catch (error) {
            console.error("Bulk update error", error);
            setChecklist(originalChecklist);
            showToast('error', 'Failed to update some tasks');
        }
    };

    // --- ADD MASTER TASK (ADMIN ONLY) ---
    const handleAddMasterTask = async () => {
        if (!newTaskName.trim()) {
            showToast('warning', 'Task name cannot be empty');
            return;
        }

        try {
            const res = await fetch(`${API_URL}/checklist/master`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({ task_name: newTaskName, description: '' })
            });

            if (res.ok) {
                setNewTaskName('');
                setIsAddingTask(false);
                showToast('success', 'New item added to Master Checklist');
                if (selectedEmp) fetchChecklist(selectedEmp.id);
            } else {
                showToast('error', 'Failed to add task');
            }
        } catch (error) {
            showToast('error', 'Network error');
        }
    };

    // --- DELETE MASTER TASK (ADMIN/SUPER_ADMIN ONLY) ---
    const handleDeleteMasterTask = async (e, masterTaskId) => {
        e.stopPropagation(); // Prevent toggling the checkbox

        if (!window.confirm("Are you sure? This will remove the task globally for ALL employees.")) {
            return;
        }

        try {
            const res = await fetch(`${API_URL}/checklist/master/${masterTaskId}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (res.ok) {
                // Update local state immediately
                setChecklist(prev => prev.filter(t => t.master_task_id !== masterTaskId));
                showToast('success', 'Task deleted successfully');
            } else {
                const err = await res.json().catch(() => ({}));
                showToast('error', err.message || 'Failed to delete task');
            }
        } catch (error) {
            console.error("Delete task error", error);
            showToast('error', 'Network error deleting task');
        }
    };

    // --- ACTIONS ---

    const handleAddClick = () => {
        setFormData(initialFormState);
        setIsEditMode(false);
        setEditEmpId(null);
        setViewMode('form');
    };

    const handleEditClick = (e, emp) => {
        e.stopPropagation();

        let formattedDOB = '';
        if (emp.birth_date) {
            if (emp.birth_date.includes('-') && emp.birth_date.split('-')[0].length === 2) {
                const [d, m, y] = emp.birth_date.split('-');
                formattedDOB = `${y}-${m}-${d}`;
            } else {
                formattedDOB = emp.birth_date.split('T')[0];
            }
        }

        setFormData({
            first_name: emp.first_name || '',
            last_name: emp.last_name || '',
            emp_code: emp.emp_code || '',
            email: emp.email || '',
            designation: emp.designation_id || '',
            department: emp.department_id || '',
            vendor_type: emp.vendor_type || '',
            address: emp.address || '',
            city: emp.city || '',
            state_id: emp.state_id || '',
            pincode: emp.pincode || '',
            birth_date: formattedDOB,
            pan_no: emp.pan_no || '',
            blood_group: emp.blood_group || '',
            sex: emp.sex || '',
            status_id: emp.status_id || '',
            current_salary: emp.current_salary || '',
            acno: emp.acno || '',
            ifsc_code: emp.ifsc_code || '',
            bankname_id: emp.bankname_id || '',
            branch: emp.branch || '',
            contact_details: emp.contact_details || ''
        });

        setEditEmpId(emp.id);
        setIsEditMode(true);
        setViewMode('form');
    };

    const handleStatusToggle = async (e, emp) => {
        e.stopPropagation();
        const action = emp.active ? 'deactivate' : 'reactivate';

        if (!window.confirm(`Are you sure you want to ${action} ${emp.first_name} ${emp.last_name}?`)) {
            return;
        }

        const endpoint = emp.active
            ? `/hr/employees/${emp.id}/deactivate`
            : `/hr/employees/${emp.id}/reactivate`;

        try {
            const res = await fetch(`${API_URL}${endpoint}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            });

            if (res.ok) {
                showToast('success', `User ${action}d successfully`);
                fetchEmployees(pagination.page);
            } else {
                const err = await res.json().catch(() => ({}));
                showToast('error', err.message || `Failed to ${action} user`);
            }
        } catch (error) {
            console.error("Status toggle error", error);
            showToast('error', 'Network error occurred');
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        const safeInt = (val) => (val && val !== '') ? parseInt(val) : 0;
        const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };

        try {
            if (isEditMode) {
                const updatePayload = {
                    first_name: formData.first_name,
                    last_name: formData.last_name,
                    birth_date: formData.birth_date,
                    address: formData.address,
                    city: formData.city,
                    state_id: safeInt(formData.state_id),
                    pincode: formData.pincode,
                    status_id: safeInt(formData.status_id),
                    blood_group: safeInt(formData.blood_group),
                    sex: safeInt(formData.sex),
                    current_salary: parseFloat(formData.current_salary) || 0
                };

                const res = await fetch(`${API_URL}/hr/employees/${editEmpId}`, {
                    method: 'PUT', headers, body: JSON.stringify(updatePayload)
                });

                if (res.ok) {
                    showToast('success', 'Employee updated successfully!');

                    if (formData.status_id) {
                        try {
                            await fetch(`${API_URL}/emails/send-status-mail`, {
                                method: 'POST',
                                headers,
                                body: JSON.stringify({
                                    userId: editEmpId,
                                    statusId: safeInt(formData.status_id)
                                })
                            });
                        } catch (emailErr) {
                            console.error('Email trigger error:', emailErr);
                        }
                    }

                    // Update Bank Details
                    if (formData.acno) {
                        await fetch(`${API_URL}/hr/employees/bank-details`, {
                            method: 'PUT', headers,
                            body: JSON.stringify({
                                emp_code: formData.emp_code,
                                account_number: formData.acno,
                                ifsc_code: formData.ifsc_code,
                                bank_name: formData.bankname_id,
                                branch_name: formData.branch,
                                contact_number: formData.contact_details
                            })
                        }).catch(e => console.error("Bank save error", e));
                    }

                    setViewMode('list');
                    await fetchEmployees(pagination.page);
                } else {
                    const errData = await res.json();
                    showToast('error', errData.message || 'Failed to update employee.');
                }
            } else {
                if (!formData.first_name || !formData.email || !formData.department) {
                    showToast('error', 'Required fields missing (Name, Email, Dept).');
                    setSubmitting(false); return;
                }

                const createPayload = {
                    ...formData,
                    designation: safeInt(formData.designation),
                    department: safeInt(formData.department),
                    current_salary: parseFloat(formData.current_salary) || 0,
                    vendor_type: safeInt(formData.vendor_type),
                    state_id: safeInt(formData.state_id),
                    blood_group: safeInt(formData.blood_group),
                    sex: safeInt(formData.sex)
                };

                const res = await fetch(`${API_URL}/hr/employees`, {
                    method: 'POST', headers, body: JSON.stringify(createPayload)
                });

                if (res.ok || res.status === 201) {
                    // Save Bank Details (if provided)
                    if (formData.acno) {
                        try {
                            await fetch(`${API_URL}/hr/employees/bank-details`, {
                                method: 'PUT', headers,
                                body: JSON.stringify({
                                    emp_code: formData.emp_code,
                                    account_number: formData.acno,
                                    ifsc_code: formData.ifsc_code,
                                    bank_name: formData.bankname_id,
                                    branch_name: formData.branch,
                                    contact_number: formData.contact_details
                                })
                            });
                        } catch (be) { console.error("Bank save error on create", be); }
                    }

                    showToast('success', 'Employee created successfully!');
                    setViewMode('list');
                    await fetchEmployees(1);
                } else {
                    const errData = await res.json();
                    showToast('error', errData.message || 'Failed to create employee.');
                }
            }
        } catch (e) {
            showToast('error', 'Network error.');
            console.error(e);
        } finally {
            setSubmitting(false);
        }
    };

    // --- IMPORT LOGIC ---
    const downloadTemplate = async () => {
        try {
            const res = await fetch(`${API_URL}/hr/employees/import-template`, {
                method: 'GET', headers: getAuthHeaders()
            });
            if (!res.ok) throw new Error("Download failed");

            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'employee_import_template.csv';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            showToast('success', 'Template downloaded successfully');
        } catch (e) {
            console.error(e);
            showToast('error', 'Failed to download template');
        }
    };

    const parseCSV = (text) => {
        const cleanText = text.replace(/^\uFEFF/, '');
        const lines = cleanText.split(/\r\n|\n/).filter(l => l.trim());
        if (lines.length < 2) return [];
        const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, '').toLowerCase());

        const result = [];
        const csvSplit = /,(?=(?:(?:[^"]*"){2})*[^"]*$)/;

        for (let i = 1; i < lines.length; i++) {
            const row = lines[i].split(csvSplit).map(val => val.trim().replace(/^"|"$/g, ''));
            if (row.length === headers.length || row.length === headers.length + 1) {
                let obj = {};
                headers.forEach((h, idx) => obj[h] = row[idx]);
                result.push({ ...obj, _rowNum: i + 1 });
            }
        }
        return result;
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.type.includes('spreadsheet')) {
            showToast('error', 'Excel (.xlsx) files are not supported. Please Save As .csv format.');
            return;
        }

        const reader = new FileReader();
        reader.onload = (evt) => {
            const text = evt.target.result;
            const data = parseCSV(text);
            validateCSVData(data, file);
        };
        reader.readAsText(file);
    };

    const validateCSVData = (data, file) => {
        const errors = [];
        const required = ['first_name', 'email', 'emp_code', 'vendor_type'];

        if (data.length === 0) {
            errors.push("File appears to be empty or has no data rows.");
        }

        data.forEach((row) => {
            required.forEach(field => {
                const key = Object.keys(row).find(k => k.includes(field));
                if (!key || !row[key]) {
                    errors.push(`Row ${row._rowNum}: Missing required field '${field}'`);
                }
            });

            const emailKey = Object.keys(row).find(k => k.includes('email'));
            if (emailKey && row[emailKey] && !/\S+@\S+\.\S+/.test(row[emailKey])) {
                errors.push(`Row ${row._rowNum}: Invalid email format (${row[emailKey]})`);
            }
        });

        if (errors.length > 50) {
            const len = errors.length;
            errors.length = 50;
            errors.push(`...and ${len - 50} more errors.`);
        }

        setImportErrors(errors);
        setImportFile(file);
        setImportSummary(null);
    };

    const handleImportSubmit = async () => {
        if (!importFile || importErrors.length > 0) return;

        setSubmitting(true);
        const fData = new FormData();
        fData.append('file', importFile);

        try {
            const res = await fetch(`${API_URL}/hr/employees/bulk-import`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: fData
            });

            const result = await res.json();

            if (res.ok) {
                const successCount = result.success_count || 0;
                const failedCount = result.failed_count || 0;

                const failedDetails = (result.details || [])
                    .filter(item => item.status === 'Failed')
                    .map(item => ({
                        row: item.row,
                        emp_code: item.emp_code,
                        error: item.message
                    }));

                setImportSummary({
                    total: result.total,
                    success: successCount,
                    failed: failedCount,
                    errors: failedDetails
                });

                fetchEmployees(1);

                if (successCount === 0 && failedCount > 0) {
                    showToast('error', 'Import failed. No records were added.');
                } else if (successCount > 0 && failedCount > 0) {
                    showToast('warning', `Import finished partially with ${failedCount} errors.`);
                } else {
                    showToast('success', 'All records imported successfully.');
                }

            } else {
                showToast('error', result.error || 'Import failed on server.');
            }
        } catch (e) {
            console.error(e);
            showToast('error', 'Network error during upload.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleReUpload = () => {
        setImportFile(null);
        setImportErrors([]);
        setImportSummary(null);
    };

    const closeImportModal = () => {
        setViewMode('list');
        setImportFile(null);
        setImportErrors([]);
        setImportSummary(null);
    };

    const getImportStatusIcon = () => {
        if (!importSummary) return null;
        if (importSummary.success === 0 && importSummary.failed > 0) return <IoCloseCircle size={60} color="#DC2626" />;
        if (importSummary.failed > 0) return <IoWarning size={60} color="#D97706" />;
        return <IoCheckmarkCircle size={60} color="#059669" />;
    };

    const getImportStatusTitle = () => {
        if (!importSummary) return '';
        if (importSummary.success === 0 && importSummary.failed > 0) return 'Import Failed';
        if (importSummary.failed > 0) return 'Import Completed with Errors';
        return 'Import Complete';
    };

    const formatDocType = (type) => {
        if (!type) return 'Document';
        return type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    // --- OTHER HELPERS ---
    const filteredEmployees = React.useMemo(() => {
        let result = employees.filter(emp => !searchTerm || `${emp.first_name} ${emp.last_name} ${emp.emp_code} ${emp.email}`.toLowerCase().includes(searchTerm.toLowerCase()));
        
        // Apply sorting
        if (sortOrder === 'asc') {
            result = [...result].sort((a, b) => (a.first_name || '').localeCompare(b.first_name || ''));
        } else if (sortOrder === 'desc') {
            result = [...result].sort((a, b) => (b.first_name || '').localeCompare(a.first_name || ''));
        } else if (sortOrder === 'recent') {
            result = [...result].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
        }
        
        return result;
    }, [employees, searchTerm, sortOrder]);

    const handleCardClick = (emp) => {
        let filled = 0, total = 8;
        if (emp.pan_no) filled++; if (emp.address) filled++; if (emp.birth_date) filled++;
        if (emp.blood_group) filled++; if (emp.email) filled++; if (emp.emp_code) filled++;
        if (emp.pincode && emp.pincode !== "000000") filled++;
        setSelectedEmp({ ...emp, progress: Math.round((filled / total) * 100) || 10 });
    };

    if (viewMode === 'assign_documents') {
        return <HRDocumentAssignment onBack={() => setViewMode('list')} />;
    }

    return (
        <div className="onboarding-page">
            <Toast show={toast.show} type={toast.type} message={toast.message} onClose={() => setToast({ ...toast, show: false })} />

            {/* Import Modal */}
            {viewMode === 'import' && (
                <div className="form-overlay">
                    <div className="form-card" style={{ width: 600 }}>
                        <div className="form-header-modal">
                            <h3>Bulk Import Employees</h3>
                            <button className="btn-icon" onClick={closeImportModal}><IoCloseOutline size={24} /></button>
                        </div>
                        <div className="form-body-modal">
                            {!importSummary ? (
                                <>
                                    <div className="drop-zone" onClick={() => fileInputRef.current.click()}>
                                        <input type="file" ref={fileInputRef} hidden accept=".csv" onChange={handleFileChange} />
                                        <div className="drop-zone-content">
                                            <IoCloudUploadOutline size={48} color="#94A3B8" />
                                            <div><strong>Click to upload</strong> or drag and drop</div>
                                            <div style={{ fontSize: 12 }}>CSV files only</div>
                                        </div>
                                    </div>

                                    {importFile && (
                                        <div className="file-info">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <IoDocumentTextOutline size={20} /> {importFile.name}
                                            </div>
                                            <IoClose style={{ cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setImportFile(null); setImportErrors([]) }} />
                                        </div>
                                    )}

                                    {importErrors.length > 0 && (
                                        <div className="validation-summary">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, color: '#DC2626', marginBottom: 8 }}>
                                                <IoAlertCircleOutline /> Validation Errors found:
                                            </div>
                                            <div style={{ maxHeight: 150, overflowY: 'auto' }}>
                                                {importErrors.map((err, i) => <div key={i} className="val-error-row">• {err}</div>)}
                                            </div>
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
                                        <button className="btn-secondary" onClick={downloadTemplate}>
                                            <IoDownloadOutline /> Download Template
                                        </button>
                                        <button
                                            className="btn-primary-add"
                                            disabled={!importFile || importErrors.length > 0 || submitting}
                                            onClick={handleImportSubmit}
                                        >
                                            {submitting ? 'Uploading...' : 'Import Data'}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <div className="import-results">
                                    <div style={{ marginBottom: 20 }}>
                                        {getImportStatusIcon()}
                                        <h3 style={{ marginTop: 10, color: importSummary.failed > 0 ? (importSummary.success === 0 ? '#DC2626' : '#D97706') : '#059669' }}>
                                            {getImportStatusTitle()}
                                        </h3>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-around', background: '#F8FAFC', padding: 15, borderRadius: 12 }}>
                                        <div className="result-stat">Total<br />{importSummary.total}</div>
                                        <div className="result-stat success-text">Success<br />{importSummary.success}</div>
                                        <div className="result-stat error-text">Failed<br />{importSummary.failed}</div>
                                    </div>

                                    {importSummary.errors && importSummary.errors.length > 0 && (
                                        <div className="error-log-box">
                                            <div style={{ marginBottom: 5, borderBottom: '1px solid #334155', paddingBottom: 5 }}><strong>Error Log:</strong></div>
                                            {importSummary.errors.map((e, i) => (
                                                <div key={i} style={{ marginBottom: 6, borderBottom: '1px dashed #334155', paddingBottom: 4 }}>
                                                    <div><span style={{ color: '#F87171' }}>Row {e.row}</span> <span style={{ color: '#94A3B8' }}>({e.emp_code || 'No ID'})</span>: {e.error}</div>
                                                    <div className="error-suggestion">{getErrorSuggestion(e.error)}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div className="modal-actions-row">
                                        <button className="btn-secondary btn-half" onClick={handleReUpload}>
                                            <IoRefreshOutline /> Re-upload File
                                        </button>
                                        <button className="btn-primary-add btn-half" onClick={closeImportModal}>
                                            Done
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ADD / EDIT FORM MODAL */}
            {viewMode === 'form' && (
                <div className="form-overlay" onMouseDown={() => setViewMode('list')}>
                    <div className="form-card" onMouseDown={e => e.stopPropagation()}>
                        <div className="form-header-modal">
                            <h3>{isEditMode ? 'Edit Employee' : 'Add Employee'}</h3>
                            <button className="btn-icon" onClick={() => setViewMode('list')}><IoCloseOutline size={24} /></button>
                        </div>
                        <div className="form-body-modal">
                            <form>
                                <div className="section-title">Identity & Role</div>
                                <div className="form-grid">
                                    <div>
                                        <label className="form-label">First Name *</label>
                                        <input className="form-input" value={formData.first_name} onChange={e => setFormData({ ...formData, first_name: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="form-label">Last Name</label>
                                        <input className="form-input" value={formData.last_name} onChange={e => setFormData({ ...formData, last_name: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="form-label">Email {isEditMode ? '(Read Only)' : '*'}</label>
                                        <input className="form-input"
                                            value={formData.email}
                                            onChange={e => setFormData({ ...formData, email: e.target.value })}
                                            disabled={isEditMode}
                                            style={isEditMode ? { backgroundColor: '#E2E8F0', cursor: 'not-allowed' } : {}}
                                        />
                                    </div>
                                    <div>
                                        <label className="form-label">Code {isEditMode && '(Read Only)'}</label>
                                        <input className="form-input"
                                            value={formData.emp_code}
                                            onChange={e => setFormData({ ...formData, emp_code: e.target.value })}
                                            disabled={isEditMode}
                                            style={isEditMode ? { backgroundColor: '#E2E8F0', cursor: 'not-allowed' } : {}}
                                        />
                                    </div>

                                    <SearchableSelect required label="Department" options={masters.departments} selectedVal={formData.department} handleChange={v => setFormData({ ...formData, department: v })} disabled={isEditMode} />
                                    <SearchableSelect label="Designation" options={masters.designations} selectedVal={formData.designation} handleChange={v => setFormData({ ...formData, designation: v })} disabled={isEditMode} />

                                    <div className="full-width">
                                        <SearchableSelect label="Employment Type" options={masters.vendorTypes} selectedVal={formData.vendor_type} handleChange={v => setFormData({ ...formData, vendor_type: v })} disabled={isEditMode} />
                                    </div>

                                    <div className="full-width">
                                        <label className="form-label">Gross Monthly Salary</label>
                                        <input
                                            type="number"
                                            className="form-input"
                                            placeholder="e.g. 25000"
                                            value={formData.current_salary}
                                            onChange={e => setFormData({ ...formData, current_salary: e.target.value })}
                                        />
                                    </div>

                                    {isEditMode && (
                                        <div className="full-width" style={{ marginTop: 10 }}>
                                            <SearchableSelect label="Current Status" options={masters.statuses} selectedVal={formData.status_id} handleChange={v => setFormData({ ...formData, status_id: v })} />
                                        </div>
                                    )}
                                </div>

                                <div className="section-title" style={{ marginTop: 24 }}>Personal & Location</div>
                                <div className="form-grid">
                                    <div><label className="form-label">Date of Birth</label><input type="date" className="form-input" value={formData.birth_date} onChange={e => setFormData({ ...formData, birth_date: e.target.value })} /></div>
                                    <SearchableSelect label="Sex" options={masters.sex} selectedVal={formData.sex} handleChange={v => setFormData({ ...formData, sex: v })} />
                                    <SearchableSelect label="Blood Group" options={masters.bloodGroups} selectedVal={formData.blood_group} handleChange={v => setFormData({ ...formData, blood_group: v })} />

                                    <div className="full-width"><label className="form-label">PAN</label><input className="form-input" value={formData.pan_no} onChange={e => setFormData({ ...formData, pan_no: e.target.value.toUpperCase() })} disabled={isEditMode} style={isEditMode ? { backgroundColor: '#E2E8F0' } : {}} /></div>

                                    <div className="full-width"><label className="form-label">Address</label><input className="form-input" value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })} placeholder="Street Address" /></div>
                                    <div><label className="form-label">City</label><input className="form-input" value={formData.city} onChange={e => setFormData({ ...formData, city: e.target.value })} /></div>
                                    <SearchableSelect label="State" options={masters.states} selectedVal={formData.state_id} handleChange={v => setFormData({ ...formData, state_id: v })} />
                                    <div><label className="form-label">Pincode</label><input className="form-input" value={formData.pincode} onChange={e => setFormData({ ...formData, pincode: e.target.value })} /></div>
                                </div>

                                <div className="section-title" style={{ marginTop: 24 }}>Bank Details</div>
                                <div className="form-grid">
                                    <div><label className="form-label">Account Number</label><input className="form-input" value={formData.acno || ''} onChange={e => setFormData({ ...formData, acno: e.target.value })} placeholder="Account No" /></div>
                                    <SearchableSelect label="Bank Name" options={masters.bankNames} selectedVal={formData.bankname_id} handleChange={v => setFormData({ ...formData, bankname_id: v })} />
                                    <div><label className="form-label">IFSC Code</label><input className="form-input" value={formData.ifsc_code || ''} onChange={e => setFormData({ ...formData, ifsc_code: e.target.value.toUpperCase() })} placeholder="IFSC" /></div>
                                    <div><label className="form-label">Branch</label><input className="form-input" value={formData.branch || ''} onChange={e => setFormData({ ...formData, branch: e.target.value })} placeholder="Branch Name" /></div>
                                    <div className="full-width"><label className="form-label">Contact (Bank)</label><input className="form-input" value={formData.contact_details || ''} onChange={e => setFormData({ ...formData, contact_details: e.target.value })} placeholder="Registered Mobile No" /></div>
                                </div>
                            </form>
                        </div>
                        <div className="form-footer-modal">
                            <button className="btn-primary-add" onClick={handleFormSubmit} disabled={submitting}>
                                {submitting ? (isEditMode ? 'Updating...' : 'Saving...') : (isEditMode ? 'Update Employee' : 'Save Employee')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="onboarding-header">
                <div className="header-content"><h1>Employee Management</h1></div>
                <div className="header-actions">
                    <button className="btn-secondary" onClick={() => setViewMode('assign_documents')}><IoDocumentTextOutline size={18} /> Assign Docs</button>
                    <button className="btn-secondary" onClick={() => setViewMode('import')}><IoCloudUploadOutline size={18} /> Import CSV</button>
                    <button className="btn-primary-add" onClick={handleAddClick}><IoAddOutline size={18} /> Add User</button>
                </div>
            </div>

            <div className="content-wrapper">
                <div className="list-section">
                    <div className="search-bar-container"><span><IoSearchOutline className="search-icon" /></span><input className="search-input" placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} /></div>
                    <div className="list-header grid-config">
                        <span className="col-head">ID</span>
                        <span className="col-head col-head-sortable">
                            Name
                            <div className="col-sort-icons">
                                <span className={`sort-icon-btn ${sortOrder === 'recent' ? 'active' : ''}`} onClick={() => setSortOrder('recent')} title="Recent">
                                    <IoTime size={14} />
                                </span>
                                <span className={`sort-icon-btn ${sortOrder === 'asc' ? 'active' : ''}`} onClick={() => setSortOrder('asc')} title="A-Z">
                                    <IoArrowUp size={14} />
                                </span>
                                <span className={`sort-icon-btn ${sortOrder === 'desc' ? 'active' : ''}`} onClick={() => setSortOrder('desc')} title="Z-A">
                                    <IoArrowDown size={14} />
                                </span>
                            </div>
                        </span>
                        <span className="col-head">Department</span>
                        <span className="col-head" style={{ textAlign: 'center' }}>Status</span>
                        <span className="col-head" style={{ textAlign: 'center' }}>Action</span>
                    </div>
                    <div className="list-scroll-area">
                        {loading ? <TableSkeleton /> : (
                            <div className="employee-list-grid">
                                {filteredEmployees.length > 0 ? filteredEmployees.map(emp => (
                                    <div key={emp.id} className={`employee-row grid-config ${selectedEmp?.id === emp.id ? 'active' : ''}`} onClick={() => handleCardClick(emp)}>
                                        <div className="row-id">{emp.emp_code || 'NA'}</div>
                                        <div className="row-name"><div className="avatar-sm">{getInitials(emp.first_name, emp.last_name)}</div><span className="name-text">{emp.first_name} {emp.last_name}</span></div>
                                        <div className="row-dept-wrapper"><span className="row-dept">{getLabel(emp.department_id, 'departments')}</span></div>
                                        <div style={{ display: 'flex', justifyContent: 'center' }}><span className={`status-badge ${emp.status_name?.toLowerCase().includes('onboarding') ? 'onboarding' : 'active'}`}>{emp.status_name}</span></div>

                                        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                                            <button
                                                className="action-btn"
                                                onClick={(e) => handleStatusToggle(e, emp)}
                                                style={{
                                                    color: emp.active ? '#10B981' : '#9CA3AF',
                                                    background: emp.active ? '#ECFDF5' : '#F3F4F6',
                                                    border: 'none',
                                                    width: '32px',
                                                    height: '32px'
                                                }}
                                                title={emp.active ? "Deactivate" : "Activate"}
                                            >
                                                <IoPower size={18} />
                                            </button>
                                            <button
                                                className="action-btn"
                                                onClick={(e) => handleEditClick(e, emp)}
                                                title="Edit Employee"
                                                style={{
                                                    color: '#F59E0B',
                                                    background: '#FFFBEB',
                                                    border: 'none',
                                                    width: '32px',
                                                    height: '32px'
                                                }}
                                            >
                                                <IoPencil size={17} />
                                            </button>
                                            <button
                                                className="action-btn"
                                                title="View Details"
                                                onClick={(e) => { e.stopPropagation(); handleCardClick(emp); }}
                                                style={{
                                                    color: '#6366F1',
                                                    background: '#EEF2FF',
                                                    border: 'none',
                                                    width: '32px',
                                                    height: '32px'
                                                }}
                                            >
                                                <IoEyeOutline size={19} />
                                            </button>
                                        </div>
                                    </div>
                                )) : <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>No employees found</div>}
                            </div>
                        )}
                    </div>
                    <div className="pagination-bar">
                        <span className="page-info">Showing page {pagination.page} of {pagination.totalPages}</span>
                        <div className="page-controls"><button className="page-btn" disabled={pagination.page === 1} onClick={() => fetchEmployees(pagination.page - 1)}><IoChevronBack size={14} /> Prev</button><button className="page-btn" disabled={pagination.page === pagination.totalPages} onClick={() => fetchEmployees(pagination.page + 1)}>Next <IoChevronForward size={14} /></button></div>
                    </div>
                </div>

                {selectedEmp && (
                    <div className="details-panel">
                        <div className="panel-header">
                            <div className="panel-header-left"><div className="avatar-lg">{getInitials(selectedEmp.first_name, selectedEmp.last_name)}</div><div className="panel-user-info"><h2>{selectedEmp.first_name}</h2><span>{getLabel(selectedEmp.designation_id, 'designations')}</span></div></div>
                            <button className="close-panel-btn" onClick={() => setSelectedEmp(null)}><IoClose size={18} /></button>
                        </div>
                        <div className="panel-body">
                            <div className="detail-section">
                                <div className="section-head">Status</div>
                                <div className="status-row"><span>{selectedEmp.status_name}</span><span>{selectedEmp.progress}%</span></div>
                                <div className="prog-track"><div className="prog-fill" style={{ width: `${selectedEmp.progress}%` }}></div></div>
                            </div>

                            {/* --- CHECKLIST SECTION --- */}
                            <div className="detail-section">
                                <div className="section-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span>Onboarding Checklist</span>
                                    {/* Action Buttons Container */}
                                    <div style={{ display: 'flex', gap: 10 }}>
                                        {/* ADMIN ONLY: ADD BUTTON */}
                                        {isAdmin && (
                                            <button
                                                onClick={() => setIsAddingTask(true)}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    color: '#4F46E5',
                                                    fontSize: '11px',
                                                    fontWeight: 600,
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}
                                            >
                                                <IoAddCircleOutline size={14} /> Add Item
                                            </button>
                                        )}
                                        {/* MARK ALL BUTTON */}
                                        {checklist.length > 0 && (
                                            <button
                                                onClick={handleMarkAll}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    color: '#4F46E5',
                                                    fontSize: '11px',
                                                    fontWeight: 600,
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}
                                            >
                                                <IoCheckmarkDoneOutline size={14} />
                                                {checklist.every(t => t.is_completed) ? 'Unmark All' : 'Mark All'}
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="checklist-container">
                                    {checklistLoading ? (
                                        <div style={{ fontSize: 12, color: '#94A3B8', padding: 10 }}>Loading checklist...</div>
                                    ) : (
                                        <>
                                            {/* ADD TASK INPUT ROW (ADMIN ONLY) */}
                                            {isAddingTask && (
                                                <div className="checklist-item" style={{ background: '#F1F5F9', border: '1px dashed #4F46E5', cursor: 'default' }}>
                                                    <IoAddCircleOutline size={20} color="#4F46E5" />
                                                    <input
                                                        autoFocus
                                                        value={newTaskName}
                                                        onChange={(e) => setNewTaskName(e.target.value)}
                                                        placeholder="Enter new task name..."
                                                        onKeyDown={(e) => { if (e.key === 'Enter') handleAddMasterTask(); else if (e.key === 'Escape') setIsAddingTask(false); }}
                                                        style={{
                                                            flex: 1,
                                                            border: 'none',
                                                            background: 'transparent',
                                                            fontSize: '13px',
                                                            outline: 'none',
                                                            color: '#1E293B'
                                                        }}
                                                    />
                                                    <button onClick={handleAddMasterTask} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#10B981' }} title="Save">
                                                        <IoSaveOutline size={16} />
                                                    </button>
                                                    <button onClick={() => { setIsAddingTask(false); setNewTaskName(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444' }} title="Cancel">
                                                        <IoClose size={16} />
                                                    </button>
                                                </div>
                                            )}

                                            {checklist.length > 0 ? (
                                                checklist.map((task) => (
                                                    <div key={task.master_task_id} className={`checklist-item ${task.is_completed ? 'completed' : ''}`} onClick={() => handleChecklistToggle(task)}>
                                                        <div className="checkbox-icon">
                                                            {task.is_completed ?
                                                                <IoCheckboxOutline size={20} color="#10B981" /> :
                                                                <IoSquareOutline size={20} color="#94A3B8" />
                                                            }
                                                        </div>
                                                        <div className="checklist-text">{task.task_name}</div>

                                                        {/* ADMIN ONLY: DELETE BUTTON */}
                                                        {isAdmin && (
                                                            <button
                                                                className="btn-icon-delete"
                                                                onClick={(e) => handleDeleteMasterTask(e, task.master_task_id)}
                                                                style={{
                                                                    marginLeft: 'auto',
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    cursor: 'pointer',
                                                                    color: '#EF4444',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    padding: '4px'
                                                                }}
                                                                title="Delete Item Globally"
                                                            >
                                                                <IoTrashOutline size={16} />
                                                            </button>
                                                        )}
                                                    </div>
                                                ))
                                            ) : (
                                                !isAddingTask && <div style={{ fontSize: 13, color: '#94A3B8', fontStyle: 'italic' }}>No checklist items defined.</div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* DOCUMENT LIST IN SIDEBAR */}
                            <div className="detail-section">
                                <div className="section-head">Uploaded Documents</div>
                                {docsLoading ? (
                                    <div style={{ fontSize: 12, color: '#94A3B8', padding: 10 }}>Loading documents...</div>
                                ) : empDocuments.length > 0 ? (
                                    <div className="doc-list-mini">
                                        {empDocuments.map((doc) => (
                                            <div key={doc.id} className="doc-item-mini">
                                                <div className="doc-item-left">
                                                    <IoDocumentTextOutline className="doc-mini-icon" size={18} />
                                                    <span className="doc-mini-name">{formatDocType(doc.document_type)}</span>
                                                </div>
                                                <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="btn-view-sm">
                                                    <IoOpenOutline size={14} /> View
                                                </a>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="no-docs-placeholder">
                                        <IoAlertCircleOutline size={20} />
                                        <span>No documents uploaded yet.</span>
                                    </div>
                                )}
                            </div>

                            <div className="detail-section">
                                <div className="section-head">Employment</div>
                                <div className="data-row"><span className="d-label"><IoBusinessOutline /> Dept</span><span className="d-val">{getLabel(selectedEmp.department_id, 'departments')}</span></div>
                                <div className="data-row"><span className="d-label"><IoIdCardOutline /> Emp Code</span><span className="d-val">{selectedEmp.emp_code}</span></div>
                                <div className="data-row"><span className="d-label"><IoMailOutline /> Email</span><span className="d-val email-text" title={selectedEmp.email}>{selectedEmp.email}</span></div>
                                <div className="data-row"><span className="d-label"><IoBriefcaseOutline /> Type</span><span className="d-val">{getLabel(selectedEmp.vendor_type, 'vendorTypes')}</span></div>
                                <div className="data-row"><span className="d-label"><IoCalendarOutline /> Joined</span><span className="d-val">{formatDate(selectedEmp.created_at)}</span></div>
                            </div>

                            <div className="detail-section">
                                <div className="section-head">Personal</div>
                                <div className="data-row"><span className="d-label"><IoCalendarOutline /> DOB</span><span className="d-val">{selectedEmp.birth_date || '-'}</span></div>
                                <div className="data-row"><span className="d-label"><IoPersonOutline /> Sex</span><span className="d-val">{getLabel(selectedEmp.sex, 'sex')}</span></div>
                                <div className="data-row"><span className="d-label"><IoPersonOutline /> Blood Group</span><span className="d-val">{getLabel(selectedEmp.blood_group, 'bloodGroups')}</span></div>
                                <div className="data-row"><span className="d-label"><IoIdCardOutline /> PAN</span><span className="d-val">{selectedEmp.pan_no || '-'}</span></div>
                            </div>

                            <div className="detail-section">
                                <div className="address-card">
                                    <div className="address-head"><IoLocationOutline /> Address</div>
                                    <div className="address-body">{selectedEmp.address || 'No address provided'}</div>
                                    <div className="address-footer">
                                        <span>{selectedEmp.city || '-'}, {getLabel(selectedEmp.state_id, 'states')}</span>
                                        <span className="pincode">{selectedEmp.pincode || '-'}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Bank Details Section */}
                            <div className="detail-section">
                                <div className="section-head"><IoWalletOutline /> Bank Details</div>
                                {selectedEmp.acno || selectedEmp.ifsc_code || selectedEmp.branch ? (
                                    <>
                                        <div className="data-row">
                                            <span className="d-label"><IoCardOutline /> Account No</span>
                                            <span className="d-val">{selectedEmp.acno || '-'}</span>
                                        </div>
                                        <div className="data-row">
                                            <span className="d-label"><IoBusinessOutline /> Bank Name</span>
                                            <span className="d-val">{getLabel(selectedEmp.bankname_id, 'bankNames')}</span>
                                        </div>
                                        <div className="data-row">
                                            <span className="d-label"><IoIdCardOutline /> IFSC Code</span>
                                            <span className="d-val">{selectedEmp.ifsc_code || '-'}</span>
                                        </div>
                                        <div className="data-row">
                                            <span className="d-label"><IoLocationOutline /> Branch</span>
                                            <span className="d-val">{selectedEmp.branch || '-'}</span>
                                        </div>
                                        {selectedEmp.contact_details && (
                                            <div className="data-row">
                                                <span className="d-label"><IoMailOutline /> Contact</span>
                                                <span className="d-val">{selectedEmp.contact_details}</span>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div style={{ fontSize: 13, color: '#94A3B8', fontStyle: 'italic', padding: '10px 0' }}>
                                        No bank details provided yet.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OnboardingHR;